import React from 'react';
import SkeletonBlock from '../ui/SkeletonBlock';
import SkeletonRoot from '../ui/SkeletonRoot';

/**
 * Branded profile loading skeleton. Mirrors ProfileDisplay layout with shimmer blocks.
 */
const ProfileSkeleton: React.FC = () => {
  return (
    <SkeletonRoot loadingLabel="Loading profile">
      <div className="min-h-screen bg-white">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6 rounded-2xl border border-primary-200/50 bg-primary-50/40 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <SkeletonBlock className="h-10 w-10 rounded-xl" />
                <div>
                  <SkeletonBlock className="h-4 w-40" />
                  <SkeletonBlock className="mt-2 h-3 w-28" />
                </div>
              </div>
              <SkeletonBlock className="h-9 w-32 rounded-xl" />
            </div>
          </div>

          <div className="mx-auto max-w-[1200px] space-y-6">
            <div className="rounded-2xl border border-primary-200/50 bg-white p-6 shadow-card">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1">
                  <div className="mb-4 flex items-center gap-4">
                    <SkeletonBlock className="h-16 w-16 rounded-2xl" />
                    <div>
                      <SkeletonBlock className="mb-2 h-8 w-64" />
                      <SkeletonBlock className="h-4 w-48" />
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <SkeletonBlock className="h-6 w-20 rounded-full" />
                    <SkeletonBlock className="h-6 w-16 rounded-full" />
                  </div>
                </div>
                <div className="lg:w-64">
                  <div className="rounded-xl border border-primary-200/50 bg-primary-50/50 p-4">
                    <SkeletonBlock className="mb-2 h-4 w-32" />
                    <SkeletonBlock className="mb-2 h-3 w-full rounded-full" />
                    <SkeletonBlock className="h-3 w-24" />
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-primary-200/50 bg-white p-2 shadow-card">
              <div className="flex gap-2 overflow-hidden">
                <SkeletonBlock className="h-9 w-24 rounded-xl" />
                <SkeletonBlock className="h-9 w-28 rounded-xl" />
                <SkeletonBlock className="h-9 w-36 rounded-xl" />
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-2xl border border-primary-200/50 bg-white p-6">
                <SkeletonBlock className="mb-2 h-6 w-48" />
                <SkeletonBlock className="h-4 w-72" />
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="rounded-2xl border border-primary-200/50 bg-white p-6">
                    <div className="flex items-start gap-4">
                      <SkeletonBlock className="h-12 w-12 rounded-xl" />
                      <div className="flex-1">
                        <SkeletonBlock className="mb-2 h-5 w-32" />
                        <div className="space-y-1">
                          <SkeletonBlock className="h-4 w-full" />
                          <SkeletonBlock className="h-4 w-3/4" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-primary-200/50 bg-white p-6">
              <div className="mb-6 flex items-center justify-between">
                <SkeletonBlock className="h-6 w-32" />
                <SkeletonBlock className="h-4 w-20" />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-xl border border-primary-200/50 p-4">
                    <div className="flex items-start gap-3">
                      <SkeletonBlock className="h-8 w-8 rounded" />
                      <div className="flex-1">
                        <SkeletonBlock className="mb-2 h-4 w-full" />
                        <SkeletonBlock className="mb-1 h-3 w-16" />
                        <SkeletonBlock className="mb-3 h-3 w-20" />
                        <SkeletonBlock className="h-6 w-20" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-primary-200/50 bg-white p-6">
              <div className="mb-6 flex items-center justify-between">
                <SkeletonBlock className="h-6 w-32" />
                <SkeletonBlock className="h-6 w-32 rounded-full" />
              </div>
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-xl border border-primary-200/50">
                    <div className="bg-white px-4 py-3">
                      <div className="flex items-center gap-3">
                        <SkeletonBlock className="h-8 w-8 rounded-lg" />
                        <SkeletonBlock className="h-4 w-40" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SkeletonRoot>
  );
};

export default ProfileSkeleton;
